/**
 * InstructionSheet Component
 * Sheet for creating and updating instructions
 */

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '@/components/ui/sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { DeleteDialog } from '@/components/shared';
import { useCreateInstruction, useUpdateInstruction, useDeleteInstruction } from '@/hooks';
import { useAccountUsers } from '@/hooks/use-account-users';
import { useEscalationPolicy, useHelpdeskAgents } from '@/hooks/use-escalation-policy';
import { missingEscalationFields, sentinelUsers } from '@/modules/escalation-policy/escalation-policy';
import type { Instruction, CreateInstructionDto, UpdateInstructionDto } from '@/types';
import type { AssignActionData } from '../types';
import { Textarea } from '@/components/ui/textarea';
import { InstructionActions } from './instruction-actions';
import { ActionComposer } from './action-composer';
import { useInstructionActions } from '../hooks/use-instruction-actions';
import { cn } from '@/lib/utils';

const instructionFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200, 'Name is too long'),
  type: z.enum(['standard', 'escalation']),
  description: z.string().min(1, 'Description is required')
});

type InstructionFormData = z.infer<typeof instructionFormSchema>;

interface InstructionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instruction?: Instruction | null;
}

export const InstructionSheet = ({ open, onOpenChange, instruction }: InstructionSheetProps) => {
  const isUpdateMode = !!instruction;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const createMutation = useCreateInstruction();
  const updateMutation = useUpdateInstruction();
  const deleteMutation = useDeleteInstruction();

  // Use custom hook for instruction actions
  const {
    actionData,
    activeAction,
    isActionExpanded,
    editingAction,
    handleActionClick,
    handleAssign,
    handleUnassign,
    handleActionRemove,
    handleActionSave,
    handleActionCancel,
    clearAction,
    initializeWithExisting,
    handleActionEdit
  } = useInstructionActions();

  // Assign to Agent picks from the brand's external helpdesk agents, Assign to Sentinel from the account's Sentinels.
  const { data: helpdeskAgents, isLoading: isLoadingAgents } = useHelpdeskAgents();
  const { data: accountUsers } = useAccountUsers();
  // Fetched with the sheet, so a composer opens knowing the brand's policy.
  const { data: escalationPolicy } = useEscalationPolicy();
  const agents = useMemo(() => helpdeskAgents ?? [], [helpdeskAgents]);
  const sentinels = useMemo(
    () => sentinelUsers(accountUsers ?? []).map((user) => ({ id: String(user.id), name: user.name, email: user.email })),
    [accountUsers]
  );

  // Initialize with existing action data when editing
  useEffect(() => {
    if (open && instruction) {
      initializeWithExisting(instruction.actionData);
    }
  }, [open, instruction, initializeWithExisting]);

  const form = useForm<InstructionFormData>({
    resolver: zodResolver(instructionFormSchema),
    defaultValues: {
      name: '',
      type: 'standard',
      description: ''
    }
  });

  // Reset form when dialog opens/closes or instruction changes
  useEffect(() => {
    if (open) {
      if (instruction) {
        form.reset({
          name: instruction.name,
          type: instruction.type,
          description: instruction.description || ''
        });
      } else {
        form.reset({
          name: '',
          type: 'standard',
          description: ''
        });
      }
    } else {
      // Clear actions when sheet closes
      clearAction();
    }
  }, [open, instruction, form, clearAction]);

  const onSubmit = async (data: InstructionFormData) => {
    // Assign has no composer to refuse it, so it is checked here, as a protocol step is.
    const assign = actionData?.find((action): action is AssignActionData => action.type === 'KIM_ASSIGN');
    if (assign && missingEscalationFields('assign', { ...assign, assignTarget: assign.target }, escalationPolicy).length) {
      toast.error(
        assign.target === 'sentinel'
          ? 'Assign to Sentinel needs a Sentinel, the brand policy does not set one'
          : 'Assign to Agent needs an agent or an email, the brand policy does not set one'
      );
      return;
    }
    try {
      // Determine type based on actionData - if array has items, it's escalation, otherwise standard
      // Escalation instructions MUST have actions, so if actionData is cleared, revert to standard
      const instructionType = actionData && actionData.length > 0 ? 'escalation' : 'standard';

      // Log the action data for now (will be sent to API later)
      if (actionData) {
        console.log('Action data to be saved:', actionData);
      }

      if (isUpdateMode && instruction) {
        // Check if actionData has changed (including being added or removed)
        // Compare arrays properly
        const actionDataChanged = JSON.stringify(actionData) !== JSON.stringify(instruction.actionData);

        // Debug logging
        console.log('=== UPDATE DEBUG ===');
        console.log('Form data.name:', data.name);
        console.log('Instruction name:', instruction.name);
        console.log('Names equal?', data.name === instruction.name);
        console.log('Form data.description:', data.description);
        console.log('Instruction description:', instruction.description);
        console.log('Descriptions equal?', data.description === (instruction.description || ''));
        console.log('Form type:', data.type);
        console.log('Instruction type:', instruction.type);
        console.log('instructionType (computed):', instructionType);
        console.log('actionDataChanged:', actionDataChanged);

        // Check if instruction fields have changed
        const hasInstructionChanges =
          data.name !== instruction.name ||
          data.description !== (instruction.description || '') ||
          instructionType !== instruction.type ||
          actionDataChanged;

        console.log('hasInstructionChanges:', hasInstructionChanges);

        // Update instruction fields if changed
        if (hasInstructionChanges) {
          const updateData: UpdateInstructionDto = {};
          if (data.name !== instruction.name) updateData.name = data.name;
          if (data.description !== (instruction.description || '')) updateData.description = data.description;
          if (instructionType !== instruction.type) updateData.type = instructionType;

          // Include actionData if it has changed (including when being cleared)
          // Use null instead of undefined so it survives JSON serialization
          // Empty arrays should be treated as null
          if (actionDataChanged) {
            updateData.actionData = actionData && actionData.length > 0 ? actionData : null;
          }

          console.log('Update payload:', updateData);

          await updateMutation.mutateAsync({ id: instruction.id, data: updateData });
        }

        toast.success('Instruction updated successfully');
        onOpenChange(false);
      } else {
        const createData: CreateInstructionDto = {
          name: data.name,
          description: data.description,
          type: instructionType,
          status: true
        };

        // Only include actionData if array has items
        if (actionData && actionData.length > 0) createData.actionData = actionData;

        await createMutation.mutateAsync(createData);
        toast.success('Instruction created successfully');
        onOpenChange(false);
      }
    } catch (error) {
      toast.error(isUpdateMode ? 'Failed to update instruction' : 'Failed to create instruction');
      console.error('Error saving instruction:', error);
    }
  };

  const handleDelete = async () => {
    if (!instruction) return;

    try {
      await deleteMutation.mutateAsync(instruction.id);
      toast.success('Instruction deleted successfully');
      setDeleteDialogOpen(false);
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to delete instruction');
      console.error('Error deleting instruction:', error);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          className={cn(
            'bg-white gap-0 flex flex-col [&>button]:hidden',
            isActionExpanded
              ? activeAction === 'KIM_SEND_PRIVATE_NOTE'
                ? 'w-[740px]! sm:w-[740px]!'
                : 'w-[1000px]! sm:w-[1000px]!'
              : 'w-[400px] sm:w-[400px]'
          )}
        >
          {/* Header - Always visible */}
          <SheetHeader className='flex flex-row items-center justify-between p-4'>
            <SheetTitle className='flex justify-start gap-2 items-center text-sm font-medium'>Instruction</SheetTitle>
            <div className='flex gap-2'>
              <Button
                className='shadow-none text-xs'
                type='button'
                variant='outline'
                size='sm'
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                size='sm'
                className='text-xs'
                type='button'
                disabled={createMutation.isPending || updateMutation.isPending}
                onClick={form.handleSubmit(onSubmit)}
              >
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : isUpdateMode ? 'Update' : 'Create'}
              </Button>
            </div>
          </SheetHeader>
          <Separator />

          {/* Two-column layout when expanded */}
          <div className={cn('flex-1 overflow-hidden', isActionExpanded ? 'flex' : '')}>
            {/* Left side - Form (always visible) */}
            <div className={cn('flex-1 overflow-y-auto p-4', isActionExpanded ? 'border-r' : '')}>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col h-full p-0'>
                  <div className='flex flex-col gap-4 flex-1'>
                    {/* Name Field */}
                    <FormField
                      control={form.control}
                      name='name'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className='font-medium text-xs text-gray-700'>Instruction Name</FormLabel>
                          <FormControl>
                            <Input className='text-xs h-10 rounded-2lg' placeholder='Enter instruction name' {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Type Field - RadioTabs */}
                    {/* <FormField
                    control={form.control}
                    name='type'
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <RadioTabs
                            options={[
                              { label: 'Standard', value: 'standard' },
                              { label: 'Escalation', value: 'escalation' }
                            ]}
                            value={field.value}
                            onValueChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  /> */}

                    {/* Description Field - Editor */}
                    <FormField
                      control={form.control}
                      name='description'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className='font-medium text-xs text-gray-700'>Description</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder='Enter description...'
                              rows={15}
                              {...field}
                              className='h-24 max-h-[245px] overflow-y-auto rounded-2xl text-xs md:text-xs'
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Separator />
                    {/* Hand over to human Agent */}
                    <div className='space-y-2'>
                      <FormLabel className='font-medium text-xs text-gray-700'>Hand over to human Agent ?</FormLabel>
                      {/* <FormDescription>
                        If enabled, the instruction will be handed over to a human agent for further processing.
                      </FormDescription> */}
                      <InstructionActions
                        onActionClick={handleActionClick}
                        onAssign={handleAssign}
                        onUnassign={handleUnassign}
                        agents={agents}
                        sentinels={sentinels}
                        disabled={isActionExpanded || isLoadingAgents}
                        actionData={actionData}
                        onActionEdit={handleActionEdit}
                        onActionRemove={handleActionRemove}
                      />
                    </div>
                  </div>
                </form>
              </Form>
            </div>

            {/* Right side - Action Composer (only when expanded) */}
            {isActionExpanded && activeAction && (
              <div className={cn('flex flex-col', activeAction === 'KIM_SEND_PRIVATE_NOTE' ? 'w-[340px]' : 'w-[600px]')}>
                <ActionComposer
                  actionType={activeAction}
                  onSave={(data) => {
                    handleActionSave(data);
                  }}
                  onCancel={handleActionCancel}
                  existingData={editingAction}
                />
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title='Delete Instruction'
        description='Are you sure you want to delete this instruction? This action cannot be undone.'
        itemName={instruction?.name}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};
